import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import {
  SW_TAG_ENCRYPT_AND_SAVE_IMAGES,
  SW_TAG_GENERATE_AND_SAVE_CONTEXT,
  SW_TAG_GENERATE_AND_SAVE_MOMENTS_CHUNKS,
} from '@/constants'
import {
  storeUserContextPayload,
  storeMomentsChunksPayload,
  storeMomentImagesPayloads,
} from '@/local-db'
import {
  registerSyncs,
  fallbackSaveImages,
  fallbackSaveChunks,
  fallbackUpdateUserContext,
} from '@/service-worker'
import { useEncryptionKey } from '@/store'
import { useApi, useApiClient } from '@/trpc/react'
import { InfiniteMomentsOutput } from '@/trpc/types'
import { encrypt } from '@/utils'

interface UpdateMomentArgs {
  momentId: string
  userId: string
  userContext: string
  userSettingsId: string
  zone: string
  title: string
  description: string
  tags: string[]
  date: string
  newImages?: File[]
}

const generateRandomId = () => {
  return Math.floor(Math.random() * 1000000)
}

const useUpdateMoment = ({
  onSuccess,
}: {
  onSuccess?: (momentId: string) => void
}) => {
  const apiClient = useApiClient()
  const encryptionKey = useEncryptionKey()
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      momentId,
      title,
      description,
      tags,
      date,
      userContext,
      newImages = [],
    }: UpdateMomentArgs) => {
      if (!encryptionKey) {
        throw new Error('No encrypt key found')
      }

      const textToEmbed = `${title} ${description}`.trim()

      // Encrypt the basic content first
      const [encryptedTitle, encryptedDescription] = await Promise.all([
        encrypt(title, encryptionKey),
        encrypt(description, encryptionKey),
      ])

      await Promise.all([
        apiClient.moments.update.mutate({
          id: momentId,
          title: encryptedTitle,
          momentEncrypted: encryptedDescription,
          date,
          tags: [...tags], // Will be updated later with generated tags
        }),
        // Delete existing chunks before saving new ones
        apiClient.momentChunks.deleteByMomentId.mutate({
          momentId,
        }),
      ])

      // Process the remaining tasks with service worker or fallback
      if (
        'serviceWorker' in navigator &&
        'SyncManager' in window &&
        navigator.serviceWorker.controller &&
        !navigator.serviceWorker.controller.scriptURL.includes('/push')
      ) {
        // Store data for background processing
        await Promise.all([
          storeUserContextPayload({
            id: generateRandomId(),
            encryptKey: encryptionKey,
            prevContext: userContext,
            userInput: textToEmbed,
          }),
          storeMomentsChunksPayload({
            id: generateRandomId(),
            encryptKey: encryptionKey,
            textToEmbed,
            momentId,
          }),
          newImages.length > 0
            ? storeMomentImagesPayloads(
                newImages.map((image, index) => ({
                  id: generateRandomId(),
                  momentId,
                  media: image.slice(0, image.size, image.type),
                  type: image.type,
                  fileName: image.name,
                  order: index,
                  encryptKey: encryptionKey,
                })),
              )
            : Promise.resolve(),
        ])

        // Register background sync tasks
        registerSyncs({ tag: SW_TAG_GENERATE_AND_SAVE_CONTEXT })
        registerSyncs({ tag: SW_TAG_GENERATE_AND_SAVE_MOMENTS_CHUNKS })
        if (newImages.length > 0) {
          registerSyncs({ tag: SW_TAG_ENCRYPT_AND_SAVE_IMAGES })
        }
      } else {
        // Fallback for when service worker is not available
        await Promise.all([
          fallbackUpdateUserContext({
            previousUserContext: userContext,
            input: textToEmbed,
            encryptKey: encryptionKey,
          }),
          fallbackSaveChunks({
            textToEmbed,
            momentId,
            encryptKey: encryptionKey,
          }),
          fallbackSaveImages({
            momentId,
            images: newImages,
            encryptKey: encryptionKey,
          }),
        ])
      }

      return momentId
    },
    onMutate: async (variables) => {
      if (!encryptionKey) return
      const momentsQueryKey = api.moments.infiniteMoments.infiniteQueryKey({
        limit: 10,
      })
      const momentByIdQueryKey = api.moments.getById.queryKey({
        id: variables.momentId,
      })
      await queryClient.cancelQueries({
        queryKey: momentsQueryKey,
      })
      await queryClient.cancelQueries({
        queryKey: momentByIdQueryKey,
      })
      const previousMoments = queryClient.getQueryData(momentsQueryKey)
      const previousMoment = queryClient.getQueryData(momentByIdQueryKey)

      const encryptedTitle = await encrypt(variables.title, encryptionKey)
      const encryptedDescription = await encrypt(
        variables.description,
        encryptionKey,
      )

      queryClient.setQueryData<InfiniteData<InfiniteMomentsOutput>>(
        momentsQueryKey,
        (old) => {
          if (!old) return old
          return {
            ...old,
            pages: old.pages.map((page) =>
              page.map((moment) =>
                moment.id === variables.momentId
                  ? {
                      ...moment,
                      title: encryptedTitle,
                      momentEncrypted: encryptedDescription,
                    }
                  : moment,
              ),
            ),
          }
        },
      )

      return {
        previousMoments,
        previousMoment,
        momentsQueryKey,
        momentByIdQueryKey,
      }
    },
    onError: (_error, _variables, context) => {
      if (!context) return
      queryClient.setQueryData(context.momentsQueryKey, context.previousMoments)
      queryClient.setQueryData(
        context.momentByIdQueryKey,
        context.previousMoment,
      )
    },
    onSettled: (_data, _error, variables, context) => {
      if (!context) return
      queryClient.invalidateQueries({
        queryKey: context.momentByIdQueryKey,
      })
      onSuccess?.(variables.momentId)
    },
  })
}

export default useUpdateMoment
